import { type PortableTextBlock, stegaClean } from 'next-sanity'

import { buttonSizeClasses } from '@/app/components/Button'
import { Media } from '@/app/components/Media'
import PortableText from '@/app/components/PortableText'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import { getBottomSpacingClass, getTopSpacingClass } from '@/app/utils/sectionSpacing'
import { cn, ptFontNormalHeaders } from '@/app/utils/style'
import { generateElementId } from '@/app/utils/text'
import type { SideBySideColumnsSection as SideBySideColumnsSectionProps } from '@/sanity.types'

type SideBySideColumnsComponentProps = {
  block: SideBySideColumnsSectionProps & { _key?: string }
  index: number
  pageId?: string
  pageType?: string
}

type Column = {
  content: PortableTextBlock[]
  image: any
}

export default function SideBySideColumnsSection({
  block,
  index,
  pageId,
  pageType,
}: SideBySideColumnsComponentProps) {
  if (!block?.columnLeft || !block?.columnRight) return null

  const isLCP = index < 2

  const renderColumn = (column: Column, columnKey: string, isPriority: boolean) => {
    if (!column?.content || !column?.image) return null

    return (
      <div key={columnKey} className="flex-1">
        <div className="flex flex-col gap-4xl">
          <div className="flex-1">
            <Media
              image={column.image}
              sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 420px"
              {...(isPriority
                ? { priority: true, fetchPriority: 'high' as const }
                : { loading: 'lazy' as const })}
            />
          </div>
          <div className="flex-1">
            <PortableText
              className={cn(
                ptFontNormalHeaders,
                '[&_.size-body-xl]:text-white-950',
                '[&_[class*="btn-"]]:!block [&_[class*="btn-"]]:!w-fit [&_[class*="btn-"]]:!mx-auto',
              )}
              tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
              buttonClassName={buttonSizeClasses({ lg: 'md' })}
              paragraphClassName="text-white-700"
              headingConfig={{
                h2: { default: 'h4', md: 'h4', lg: 'h2' },
              }}
              value={column.content as PortableTextBlock[]}
              documentId={pageId}
              documentType={pageType}
              parentPath={
                block._key ? `pageBuilder[_key=="${block._key}"].${columnKey}.content` : undefined
              }
              componentOverrides={{
                block: {
                  'normal': ({ children }) => (
                    <p className="size-body-xs lg:size-body-sm">{children}</p>
                  ),
                  'body-sm': ({ children }) => (
                    <p className="max-w-[428px] mx-auto size-body-sm lg:size-body-sm text-white-700">
                      {children}
                    </p>
                  ),
                  'h4': ({ children }) => {
                    const id = generateElementId(children)
                    return (
                      <p
                        id={id}
                        data-heading-id={id}
                        data-anchor-expose="true"
                        className="size-header-h4 lg:size-header-h4"
                      >
                        {children}
                      </p>
                    )
                  },
                },
              }}
              headingsAsParagraphs
            />
          </div>
        </div>
      </div>
    )
  }

  const topSpacingClass = getTopSpacingClass(stegaClean(block.topSpacing))
  const bottomSpacingClass = getBottomSpacingClass(stegaClean(block.bottomSpacing))

  return (
    <div className={cn('content-container', topSpacingClass, bottomSpacingClass)}>
      <div className="mx-auto flex max-w-[557px] flex-col md:max-w-[1144px]">
        {block.heading && <h2 className="size-header-h4 text-center mb-9xl">{block.heading}</h2>}
        <div className="flex flex-col gap-11xl md:flex-row md:gap-6xl lg:gap-10xl">
          {renderColumn(block.columnLeft as Column, 'columnLeft', isLCP)}
          {renderColumn(block.columnRight as Column, 'columnRight', false)}
        </div>
      </div>
    </div>
  )
}
